'use client';

import { useEffect, useState } from 'react';
import { cn } from '@savvy-kit/lib/utils';
import { Tag } from '@savvy-kit/patterns/tag';
import { Tag as TagIcon } from 'lucide-react';
import { tagIndicatorPropsFromTagData } from 'src/utils/advisor-dash/tag-indicator-from-tag-data';
import { getLocalStorageValue, setLocalStorageValue } from 'src/utils/local-storage';

/**
 * Inline household tags row on the household page. Lives between the page
 * title and the tab navigation. Replaces the standalone "Manage tags (N)"
 * button — Edit opens the same `ManageHouseholdTagsDialog`. Hide/Show
 * collapses the pill list with a per-pill staggered cascade (no animation
 * library — Tailwind + per-element transitionDelay). The expanded state
 * persists per-household in localStorage so an advisor's preference sticks
 * across reloads on that household specifically.
 *
 * See Linear 4C1T-115.
 */
export type HouseholdTagRow = {
  readonly id: string;
  readonly name: string;
  readonly isSmart: boolean;
  readonly hexColorCode: string | null;
  readonly emoji: string | null;
};

type HouseholdTagsRowProps = {
  readonly householdId: string;
  readonly tags: readonly HouseholdTagRow[];
  readonly disabled?: boolean;
  readonly onEdit: () => void;
};

const STORAGE_KEY_PREFIX = 'household-tags-row-expanded-';

type ExpandedState = { readonly expanded: boolean };

function isExpandedState(x: unknown): x is ExpandedState {
  if (typeof x !== 'object' || x === null || !('expanded' in x)) return false;
  return typeof x.expanded === 'boolean';
}

export function HouseholdTagsRow({ householdId, tags, disabled = false, onEdit }: HouseholdTagsRowProps) {
  const storageKey = `${STORAGE_KEY_PREFIX}${householdId}`;

  // Start expanded on first render to match SSR output. After mount we
  // sync with the user's saved preference for this household. Two-phase
  // init avoids the React 19 hydration mismatch that would otherwise fire
  // when localStorage disagrees with the server-rendered default.
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    const stored = getLocalStorageValue<ExpandedState>({ dataKey: storageKey, validator: isExpandedState });
    if (stored !== null) setExpanded(stored.expanded);
  }, [storageKey]);

  const setAndPersistExpanded = (next: boolean) => {
    setExpanded(next);
    setLocalStorageValue<ExpandedState>({ dataKey: storageKey, data: { expanded: next } });
  };

  const isEmpty = tags.length === 0;
  // When empty, Hide/Show is irrelevant — only Edit shows. Treat as expanded
  // to keep the grid-row collapse animation in its open state (no flash).
  const showPills = isEmpty || expanded;

  return (
    <div
      className="grid items-center bg-slate-100 rounded mb-4 px-4 py-3.5 text-sm transition-[grid-template-columns] duration-300 ease-in-out"
      style={{ gridTemplateColumns: showPills ? 'min-content 1fr auto' : 'min-content 0fr auto' }}
    >
      {/* Column 1: TAGS label — fixed auto column, never collapses */}
      <div className="flex items-center gap-1.5 text-secondary shrink-0 border-r border-slate-300 pr-3 py-[5px] whitespace-nowrap">
        <TagIcon className="size-3.5" aria-hidden />
        <span className="font-medium uppercase tracking-wide text-xs">Tags ({tags.length})</span>
      </div>

      {/* Column 2: pill badges — 1fr→0fr, clips as column shrinks */}
      <div
        id={`household-tags-pills-${householdId}`}
        className={cn(
          'flex flex-nowrap items-center gap-2 overflow-hidden transition-opacity duration-300 ease-in-out',
          showPills ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
        aria-hidden={!showPills}
      >
        {tags.map((tag, index) => (
          <Tag
            key={tag.id}
            type="read-only"
            {...tagIndicatorPropsFromTagData({
              emoji: tag.emoji,
              hexColorCode: tag.hexColorCode,
              isSmart: tag.isSmart,
            })}
            className={cn('border-0 bg-slate-200', index === 0 && 'ml-3')}
          >
            {tag.name}
          </Tag>
        ))}
      </div>

      {/* Column 3: action buttons */}
      <div className="flex items-center gap-3 pl-3">
        {(isEmpty || expanded) && (
          <button
            type="button"
            onClick={onEdit}
            disabled={disabled}
            aria-label={isEmpty ? 'Add household tags' : 'Edit household tags'}
            className="text-cyan-400 hover:text-cyan-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            Edit
          </button>
        )}
        {!isEmpty && (
          <button
            type="button"
            onClick={() => setAndPersistExpanded(!expanded)}
            disabled={disabled}
            aria-expanded={expanded}
            aria-controls={`household-tags-pills-${householdId}`}
            className="text-cyan-400 hover:text-cyan-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {expanded ? 'Hide' : 'Show'}
          </button>
        )}
      </div>
    </div>
  );
}
